import React from 'react';
import { useDashboardTranslation } from '../../i18n';
import { TrendingUp, CheckCircle } from 'lucide-react';
import type { WizardFormData } from './types';
import type { EconomicIndicator } from '../../utils/indicatorsCatalog';
import { normalizeCategories, type DashboardCategoryInput } from './categories';

/**
 * Vacío a propósito — mismo motivo que `DEFAULT_AVAILABLE_ROLES`: eran las categorías de Siesa
 * Comercial dentro de una librería compartida. El host declara las suyas con `availableCategories`.
 */
export const DEFAULT_WIDGET_CATEGORIES: DashboardCategoryInput[] = [];

interface Step4AppearanceProps {
  formData: WizardFormData;
  onUpdateForm: (key: keyof WizardFormData, value: unknown) => void;
  /** Unidad del indicador seleccionado (para la vista previa) */
  indicatorUnidad?: string;
  /** Bandera del país del indicador (para la vista previa) */
  indicatorBandera?: string;
  /** Indicador completo seleccionado (para badge de resumen) */
  selectedIndicator?: EconomicIndicator | null;
  /** Si está en modo catálogo (muestra badge de resumen) */
  isCatalogMode?: boolean;
  /**
   * 🔴 **El vocabulario de categorías, y desde F3 con su vertical.** Ver `DashboardCategoryOption`.
   *
   * ⚠️ **Estaba tipado `string[]` y era mentira.** El host de Comercial lleva desde el 2026-09-10
   * mandando objetos `{id, label}` —porque la etiqueta se traduce y el id se guarda—, así que el
   * `<option>` recibía un OBJETO como hijo. En React eso no se pinta: **rompe el render del paso**.
   * Se acepta la forma antigua para no dejar ninguna ventana rota entre 3.a y 3.b.
   */
  availableCategories?: DashboardCategoryInput[];
}

export const Step4Appearance: React.FC<Step4AppearanceProps> = ({
  formData,
  onUpdateForm,
  indicatorUnidad,
  indicatorBandera,
  selectedIndicator,
  isCatalogMode = false,
  availableCategories = DEFAULT_WIDGET_CATEGORIES,
}) => {
  const { t } = useDashboardTranslation();

  // La categoría en curso puede no estar en el vocabulario del host: pasa al editar un widget
  // sembrado antes, y también con el valor inicial del wizard. Si no se ofreciera como opción, el
  // `select` pintaría la primera de la lista mientras el formulario sigue guardando la otra —
  // el usuario creería haber elegido algo que no eligió.
  //
  // ⚠️ El valor inicial es la cadena vacía —la categoría es OBLIGATORIA y nace sin elegir—, y esa sí
  // se ofrece: es lo que hace que el `select` abra en blanco en vez de en la primera opción, que
  // sería elegir por la persona.
  const delHost = normalizeCategories(availableCategories);
  const categorias = delHost.some(c => c.id === formData.categoria)
    ? delHost
    : [{ id: formData.categoria, label: formData.categoria }, ...delHost];
  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <h3 className="text-lg font-semibold text-gray-800">{t('dashboard.builder.step4.title')}</h3>

      {/* Badge resumen — solo modo catálogo */}
      {isCatalogMode && selectedIndicator && (
        <div className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
          <span className="text-xl flex-shrink-0">{selectedIndicator.bandera}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate">{selectedIndicator.nombre}</p>
            <p className="text-[11px] text-gray-400">KPI · {selectedIndicator.frecuencia} · {selectedIndicator.unidad}</p>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-green-700 font-semibold flex-shrink-0">
            <CheckCircle className="w-3.5 h-3.5" /> Tipo y mapeo configurados
          </div>
        </div>
      )}

      <div className="grid gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('dashboard.customize.name')}</label>
          <input
            type="text"
            placeholder={t('dashboard.builder.step4.namePlaceholder')}
            value={formData.nombre}
            onChange={e => onUpdateForm('nombre', e.target.value)}
            className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('dashboard.builder.step4.description')}</label>
          <input
            type="text"
            value={formData.descripcion}
            onChange={e => onUpdateForm('descripcion', e.target.value)}
            className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('dashboard.customize.category')}</label>
            <select
              value={formData.categoria}
              onChange={e => onUpdateForm('categoria', e.target.value)}
              className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
            >
              {categorias.map(categoria => (
                <option key={categoria.id} value={categoria.id}>{categoria.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('dashboard.customize.width')}</label>
            <select
              value={formData.cols_default}
              onChange={e => onUpdateForm('cols_default', Number(e.target.value))}
              className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value={3}>{t('dashboard.customize.widthSmall')}</option>
              <option value={6}>{t('dashboard.customize.widthMedium')}</option>
              <option value={12}>{t('dashboard.customize.widthFull')}</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('dashboard.customize.refresh')}</label>
          <select
            value={formData.refresh_policy ?? 300}
            onChange={e => onUpdateForm('refresh_policy', Number(e.target.value))}
            className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value={0}>{t('dashboard.builder.step4.noRefresh')}</option>
            <option value={30}>{t('dashboard.builder.step4.every30s')}</option>
            <option value={60}>{t('dashboard.builder.step4.every1m')}</option>
            <option value={120}>{t('dashboard.builder.step4.every2m')}</option>
            <option value={300}>{t('dashboard.builder.step4.every5m')}</option>
            <option value={600}>{t('dashboard.builder.step4.every10m')}</option>
            <option value={1800}>{t('dashboard.builder.step4.every30m')}</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">Tiempo entre consultas automáticas al endpoint. Aplica cuando el widget está visible en el dashboard.</p>
        </div>
      </div>

      {/* Vista previa — solo KPI */}
      {formData.tipo === 'kpi' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('dashboard.builder.step4.preview')}</label>
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
            <div className="bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-100">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
                {indicatorBandera && <span className="mr-1">{indicatorBandera}</span>}
                {formData.nombre || 'Widget'}
              </p>
              <p className="text-2xl font-bold text-gray-900 mb-1 tracking-tight">
                — {indicatorUnidad ?? ''}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-green-600 font-semibold flex items-center gap-0.5">
                  <TrendingUp className="w-3 h-3" /> +0.00%
                </span>
                <span className="text-[11px] text-gray-400">· Hoy</span>
              </div>
              {formData.descripcion && (
                <p className="text-[11px] text-gray-400 mt-1">{formData.descripcion}</p>
              )}
            </div>
          </div>
          <p className="text-[11px] text-gray-400 mt-1">La vista previa muestra el layout — los valores reales se cargarán desde el endpoint.</p>
        </div>
      )}
    </div>
  );
};
